import {ref, computed, watch} from 'vue';
import _ from 'lodash';
import {useValidation} from "@/composables/useValidation";
import {useAuthService} from "@/composables/useAuthService";
import {useFormData} from "~/composables/useFormData.js";
import {parseScoreSchema} from "~/utils/scoring";
import {useHttpRequest} from "~/composables/useHttpRequest";
import {endpoints} from "~/network/endpoints.js";
import {useHandicapApi} from "~/composables/useHandicapApi";
import {sameMinute, sideName, toMs} from "~/utils/challengeSchedule";
import {scheduleDialogCopy, scheduleSavedMessage} from "~/utils/scheduleDialog";

export function useFixtureForm(initProps) {
    const inputProps = ref(initProps);
    const props = computed(() => inputProps.value ?? initProps);
    const {t} = useI18n();
    const {isPlayerInFixture, myTeamFromListOfTeams, fixtureResultAction, fixtureResultActionLabel, isFixtureFullyApproved, fixtureRequiresMyApproval} = useValidation();
    const {notify} = useListenerService();
    const {user, isAdminOfClub, isAuthenticated, isSuperUser} = useAuthService();
    const {outputDateFromDb, inputDateForDb} = useCrudConverters();
    const {suggestedTimeSelectHalfHour} = useFormData();
    const {httpRequest} = useHttpRequest();
    const scoringSystem = ref(null);
    // Goal scorers (football): rows edited by ScoringSystemInput's scorer
    // section; submitted as an atomic bulk replace of the fixture's manual
    // events AFTER the result saves. [{side, playerId, minute, ownGoal}]
    const scorers = ref([]);
    // Did the existing scorer set load cleanly? Guards saveScorers from pushing an
    // empty (destructive) bulk replace when the events fetch failed — the bulk now
    // wipes live-scored goals too, so a silent load-blip must never trigger a save.
    // Defaults true so a fresh create (which never fetches) saves normally.
    const scorersLoaded = ref(true);
    const supportsScorers = computed(() => {
        try {
            if (!scoringSystem.value?.schema) return false;
            const parsed = parseScoreSchema(scoringSystem.value.schema, scoringSystem.value.schemaParamValues);
            return (parsed.events ?? []).some(e => e.playerRequired && e.affectsScore);
        } catch {
            return false;
        }
    });
    const gameData = ref([]);
    const homeResult = ref(null);
    const awayResult = ref(null);
    // a typed-in score: null/'' (untouched or cleared cells) are blank
    const hasScore = (v) => v !== null && v !== undefined && String(v).trim() !== '' && !isNaN(parseInt(v));
    const homeGames = ref(0);
    const awayGames = ref(0);
    const homeTeamId = computed(() => props.value.homeTeamId ?? fixture.value?.homeTeamId);
    const awayTeamId = computed(() => props.value.awayTeamId ?? fixture.value?.awayTeamId);
    // A match being created has no clubId yet — take the club from the division or the
    // ladder challenge it's being created in, or an admin reads the member wording.
    const fixtureClubId = computed(() => fixture.value?.clubId
        ?? linker.value?.clubId ?? linker.value?.ladder?.clubId ?? linker.value?.ladderClubId ?? null);
    const isAdmin = computed(() => isAuthenticated.value && fixture.value && (isSuperUser.value || (fixtureClubId.value != null && isAdminOfClub(fixtureClubId.value))));
    const isMyFixture = computed(() => isAuthenticated.value && fixture.value && isPlayerInFixture(user.value.playerId, fixture.value));
    const formType = computed(() => props.value.fixtureId != null ? (isAdmin.value || needsMyApproval.value || isMyFixture.value ? "edit" : "view") : "add");
    const canEdit = computed(() => {
        if (!fixture.value) return true;
        return isAdmin.value ? fixture.value.adminEditable : (isMyFixture.value && fixture.value.playerEditable);
    });
    const canCreate = computed(() => isAuthenticated.value && props.value.fixtureId == null);
    const needsMyApproval = computed(() =>
        isAuthenticated.value && fixture.value != null &&
        fixtureRequiresMyApproval(fixture.value, {playerId: user.value?.playerId, isAdmin: isAdmin.value}));

    // Canonical result action for the current user: 'record' | 'approve' | 'edit' | null.
    const resultAction = computed(() =>
        fixtureResultAction(fixture.value, {playerId: user.value?.playerId, isAdmin: isAdmin.value}));

    // Americano fixtures on split-games systems must be recorded with full set
    // scores (the API enforces the same rule) — the game grid is pinned on and
    // save is blocked until every set is entered. Walkover outcomes (no games
    // split) stay recordable through the walkover affordance.
    const requiresGameScores = computed(() => fixture.value?.requiresGameScores === true);
    const tiebreakGameNumber = computed(() => fixture.value?.tiebreakGameNumber ?? null);
    const matchedOutcome = computed(() =>
        scoringSystem.value?.outcomes?.find(o =>
            o.homeResult === homeResult.value && o.awayResult === awayResult.value) ?? null);
    const requiredGamesSatisfied = () => {
        const outcome = matchedOutcome.value;
        if (!outcome) return false;
        if (outcome.homeGames == null || outcome.awayGames == null) return true; // walkover
        const rows = gameData.value.filter(g => {
            const home = parseInt(g.homeTeamScore);
            const away = parseInt(g.awayTeamScore);
            return !isNaN(home) && !isNaN(away) && home !== away;
        });
        return rows.length === outcome.homeGames + outcome.awayGames;
    };

    // Schedule dialog wording (spec R4, shared with the ladder challenge page's
    // When & where card). The API confirms any time a CLUB admin sends straight
    // away — even one who plays in the match — so an admin's label says "Set".
    // A member's time is a proposal until the other side sends the same minute.
    const isScheduleAdmin = computed(() => isAuthenticated.value && fixtureClubId.value != null && isAdminOfClub(fixtureClubId.value));
    const scheduleSides = computed(() => {
        const f = fixture.value;
        if (!f) return {mine: null, theirs: null};
        const myTeam = myTeamFromListOfTeams(user.value?.playerId, [f.homeTeam, f.awayTeam]);
        if (!myTeam) return {mine: null, theirs: null, myTeam: null};
        const isHome = myTeam.id === f.homeTeamId;
        return {
            myTeam,
            mine: isHome ? f.homeTeamScheduledAt : f.awayTeamScheduledAt,
            theirs: isHome ? f.awayTeamScheduledAt : f.homeTeamScheduledAt,
        };
    });
    // The value the dialog would send, in the API's UTC form.
    const pickedScheduleValue = computed(() => {
        const f = fixture.value;
        if (!f?.date || !f?.time) return null;
        return inputDateForDb(f.date + " " + f.time, 'yyyy-MM-dd HH:mm');
    });
    // The time a save would CONFIRM (as stored), or null: the viewer plays and
    // the picked minute is the other side's open proposal. A non-playing admin
    // never "confirms" — they set the time for both sides.
    const confirmsProposal = computed(() => {
        if (!props.value.isScheduleUpdate || !pickedScheduleValue.value) return null;
        if (fixture.value?.scheduledAt) return null;
        const target = scheduleSides.value.theirs;
        return target && sameMinute(pickedScheduleValue.value, target) ? target : null;
    });
    // The other side's name, for the note and the toast.
    const otherSideName = computed(() => {
        const f = fixture.value;
        const mine = scheduleSides.value.myTeam;
        if (!f || !mine) return null;
        const other = mine.id === f.homeTeamId ? f.awayTeam : f.homeTeam;
        // a ladder match: the challenge's side display name (ladder entry name,
        // else team; doubles "A + B")
        const lc = f.ladderChallenge ?? (linkerNamespace.value === 'ladderChallenges' ? linker.value : null);
        const otherId = other?.id ?? (mine.id === f.homeTeamId ? f.awayTeamId : f.homeTeamId);
        if (lc && otherId) {
            if (otherId === lc.challengingTeamId && lc.challengingTeamDisplayName) return lc.challengingTeamDisplayName;
            if (otherId === lc.challengedTeamId && lc.challengedTeamDisplayName) return lc.challengedTeamDisplayName;
        }
        return sideName(other, null);
    });
    // Title (matching the button that opened the dialog, when it says) + note.
    const scheduleCopy = computed(() => scheduleDialogCopy({
        mode: props.value.scheduleMode ?? null,
        isCreate: !!props.value.isScheduleCreate,
        isAdmin: isScheduleAdmin.value,
        inFixture: !!scheduleSides.value.myTeam,
        scheduledAt: fixture.value?.scheduledAt ?? null,
        mine: scheduleSides.value.mine ?? null,
        theirs: scheduleSides.value.theirs ?? null,
        confirms: confirmsProposal.value,
        otherName: otherSideName.value,
    }));
    const scheduleTitle = computed(() => scheduleCopy.value.title);
    // One line under the picker saying what saving does.
    const scheduleNote = computed(() => (isScheduleForm.value ? scheduleCopy.value.note : null));
    // Set when saving would confirm the other side's time from "Set another time".
    const scheduleBlock = computed(() => (isScheduleForm.value ? scheduleCopy.value.block : null));
    const {setAlertMessage} = useAlertService();
    // Ladder matches can't be scheduled in the past (the API refuses more than
    // 5 minutes back), except re-sending the unchanged confirmed time.
    const pastLadderTime = (picked) => {
        const f = fixture.value;
        const isLadder = !!(f?.ladderChallengeId || props.value.schedulingLadderChallengeId || props.value.ladderChallengeId);
        if (!isLadder || !picked) return false;
        if (f?.scheduledAt && sameMinute(picked, f.scheduledAt)) return false;
        const ms = toMs(picked);
        return ms != null && ms < Date.now() - 5 * 60_000;
    };
    const toastScheduleSaved = (confirms) => setAlertMessage({
        message: scheduleSavedMessage(scheduleFixture.value, {confirms: !!confirms, isAdmin: isScheduleAdmin.value, otherName: otherSideName.value}),
        color: 'success',
    });

    const title = computed(() => {
        if (isScheduleForm.value && (fixture.value || props.value.isScheduleCreate)) return scheduleTitle.value;
        if (!props.value.fixtureId) return t('create_fixture');
        // Result fixture: header mirrors the action the user is taking.
        if (isAdmin.value || isMyFixture.value) return fixtureResultActionLabel(resultAction.value);
        return t('view_fixtures', 1);
    });

    const linkerNamespace = computed(() => {
        if (props.value.leagueCycleDivisionId != null) return "leagueCycleDivisions";
        if (props.value.ladderChallengeId != null || props.value.schedulingLadderChallengeId) return "ladderChallenges";
        if (props.value.tournamentStageId != null || props.value.schedulingTournamentStageId) return "tournamentStages";
        return null;
    });

    const linkerId = computed(() => {
        if (props.value.leagueCycleDivisionId != null) return props.value.leagueCycleDivisionId;
        if (props.value.ladderChallengeId != null) return props.value.ladderChallengeId;
        if (props.value.schedulingLadderChallengeId != null) return props.value.schedulingLadderChallengeId;
        if (props.value.tournamentStageId != null) return props.value.tournamentStageId;
        if (props.value.schedulingTournamentStageId != null) return props.value.schedulingTournamentStageId;
        return null;
    });

    // Handicap start for a pairing that has NO fixture yet (record result from a league / tournament /
    // americano page): the saved fixture and the ladder challenge carry their own handicapStart; here
    // the server resolves it from the two team ids and the competition link.
    const {getPairingStart} = useHandicapApi();
    const pairingHandicapStart = ref(null);
    const pairingKey = computed(() => {
        const p = props.value;
        if (p.fixtureId || !p.homeTeamId || !p.awayTeamId) return null;
        const link = p.leagueCycleDivisionId ? `leagueCycleDivisionId=${p.leagueCycleDivisionId}`
            : p.tournamentStageId ? `tournamentStageId=${p.tournamentStageId}`
            : p.americanoCycleDivisionId ? `americanoCycleDivisionId=${p.americanoCycleDivisionId}` : null;
        return link ? `${p.homeTeamId}|${p.awayTeamId}|${link}` : null;
    });
    watch(pairingKey, async (key) => {
        pairingHandicapStart.value = null;
        if (!key) return;
        const p = props.value;
        try {
            const res = await getPairingStart({
                homeTeamId: p.homeTeamId, awayTeamId: p.awayTeamId,
                leagueCycleDivisionId: p.leagueCycleDivisionId || undefined,
                tournamentStageId: p.tournamentStageId || undefined,
                americanoCycleDivisionId: p.americanoCycleDivisionId || undefined,
            });
            if (pairingKey.value === key) pairingHandicapStart.value = res;
        } catch {
            pairingHandicapStart.value = null;
        }
    }, {immediate: true});
    const handicapStart = computed(() => fixture.value?.handicapStart ?? linker.value?.handicapStart ?? pairingHandicapStart.value ?? null);

    const noEditReason = computed(() => {
        if (!fixture.value) return null;
        if (isAdmin.value) return fixture.value.adminNonEditableReason;
        if (isMyFixture.value) return fixture.value.playerNonEditableReason;
        return null;
    });

    const buttonText = computed(() => {
        if (isScheduleForm.value && (fixture.value || props.value.isScheduleCreate)) {
            return scheduleTitle.value;
        }
        // Record result / Approve result / Edit result — driven by the canonical action.
        return fixtureResultActionLabel(resultAction.value);
    });

    const isApproved = computed(() => {
        return fixture.value != null && fixture.value.approvedAt != null;
    });

    // One-line context note for the score dialog. Deliberately describes the fixture's approval
    // STATE, never who "entered" the result: approval is tracked per team, and admin-managed
    // players have no user account so they never enter or approve anything themselves — naming
    // them as having entered/approved a result is simply wrong.
    const statusNote = computed(() => {
        if (!fixture.value || isScheduleForm.value) return null;
        if (noEditReason.value) return noEditReason.value;
        switch (resultAction.value) {
            case 'approve':
                // An admin can approve/override whichever side is still pending; a plain
                // participant is being asked for their own side's approval.
                return isAdmin.value
                    ? 'As an admin, you can approve this result now.'
                    : 'This result is awaiting your approval. Check it and confirm.';
            case 'edit':
                return isFixtureFullyApproved(fixture.value)
                    ? null
                    : 'This result is awaiting approval from the other side. You can still edit it.';
            case 'record':
                if (isAdmin.value) return 'As an admin, this result will be approved as soon as you record it.';
                // Competitions that auto-approve count a member's result straight away.
                return competitionAutoApproves.value
                    ? 'This competition approves results automatically, so it counts as soon as you record it.'
                    : 'The other side will need to approve this result before it counts.';
            default:
                return null;
        }
    });

    const save = async () => {
        if (props.value.isDelete) {
            await deleteFixture(fixture.value.id);
            if (!hasFixtureErrors.value) {
                notify('fixtureUpdated', fixture.value);
            }
        } else if (props.value.isScheduleUpdate) {
            // Rebuilt from a date and an HH:mm; when that minute is the proposal
            // being answered, send the proposal exactly as stored so the API
            // reads it as a confirm, never a counter-proposal.
            const picked = inputDateForDb(fixture.value.date + " " + fixture.value.time, 'yyyy-MM-dd HH:mm');
            if (pastLadderTime(picked)) {
                scheduleFormErrors.value = {global: ['That time has already passed.']};
                return;
            }
            if (scheduleCopy.value.block) {
                scheduleFormErrors.value = {global: [scheduleCopy.value.block]};
                return;
            }
            const confirms = confirmsProposal.value;
            const suggestedDate = confirms ?? picked;

            const data = {
                suggestedDate: suggestedDate,
                playingAreaId: fixture.value.playingAreaId || null
            };

            await updateFixtureSchedule(fixture.value.id, data);
            if (!hasScheduleFormError.value) {
                toastScheduleSaved(confirms);
                notify('fixtureUpdated', scheduleFixture.value);
            }
        } else if (props.value.isScheduleCreate) {
            const suggestedDate = inputDateForDb(fixture.value.date + " " + fixture.value.time, 'yyyy-MM-dd HH:mm');
            if (pastLadderTime(suggestedDate)) {
                scheduleFormErrors.value = {global: ['That time has already passed.']};
                return;
            }

            const data = {
                homeTeamId: fixture.value.homeTeamId,
                awayTeamId: fixture.value.awayTeamId,
                ladderChallengeId: fixture.value.ladderChallengeId || null,
                leagueCycleDivisionId: fixture.value.leagueCycleDivisionId || null,
                suggestedDate: suggestedDate,
                clubId: linker.value?.clubId || null,
                sportId: fixture.value.sportId,
                scoringSystemId: fixture.value.scoringSystemId,
                playingAreaId: fixture.value.playingAreaId || null
            };

            await createFixtureSchedule(data);
            if (!hasScheduleFormError.value) {
                toastScheduleSaved(false);
                notify('fixtureUpdated', scheduleFixture.value);
            }
        } else {
            if (requiresGameScores.value && homeResult.value != null && !requiredGamesSatisfied()) {
                fixtureErrors.value = {
                    global: ['Full set scores are required for this americano. Enter every set ' +
                        '(and the tiebreak if one was played), or record a walkover.'],
                };
                return;
            }
            // A "-" result means the entered score resolved to no outcome. The
            // common case is a level score on a no-draw system (basketball) —
            // say so in sport terms instead of surfacing the server's
            // validation wording.
            if (homeResult.value === '-' || awayResult.value === '-') {
                fixtureErrors.value = {
                    global: [homeGames.value === awayGames.value
                        ? "This scoring system has no draw, so a level score can't be saved. " +
                          "Play overtime and enter the final score."
                        : "That score doesn't match any outcome for this scoring system."],
                };
                return;
            }
            await createFixture(prepareSaveData());
            if (!hasFixtureErrors.value) {
                await saveScorers();
                notify('fixtureUpdated', fixture.value);
            }
        }

    };

    const prepareSaveData = () => {
        return {
            // Send the id when editing so the backend loads the exact fixture (Get(request.Id))
            // instead of a fuzzy team-matchup lookup — the latter can't find club-less friendly
            // fixtures and ends up inserting a duplicate (save error).
            id: props.value.fixtureId || fixture.value?.id,
            homeTeamId: homeTeamId.value,
            awayTeamId: awayTeamId.value,
            homeTeamResult: homeResult.value,
            awayTeamResult: awayResult.value,
            approve: true,
            scoringSystemId: scoringSystem.value.id,
            // a cell typed into and cleared holds '' — that is a blank game, not a score
            games: scoringSystem.value.splitGames ? gameData.value.filter(g => hasScore(g.homeTeamScore) && hasScore(g.awayTeamScore)).map((g, i) => ({
                homeTeamScore: _.parseInt(g.homeTeamScore),
                awayTeamScore: _.parseInt(g.awayTeamScore),
                sort: i
            })) : null,
            leagueCycleDivisionId: props.value.leagueCycleDivisionId || fixture.value?.leagueCycleDivisionId,
            ladderChallengeId: props.value.ladderChallengeId || fixture.value?.ladderChallengeId,
            tournamentStageId: props.value.tournamentStageId || fixture.value?.tournamentStageId,
            // Americano matches are pre-generated per cycle (like meet fixtures): result
            // entry always goes by fixture id — just carry the division link through.
            americanoCycleDivisionId: props.value.americanoCycleDivisionId || fixture.value?.americanoCycleDivisionId
        };
    };

    const calculateScore = () => {
        if (gameData.value.length === 0) return;
        if (!scoringSystem.value.splitGames) return;
        homeGames.value = 0;
        awayGames.value = 0;
        let completedGames = 0;
        gameData.value.forEach(game => {
            const home = parseInt(game.homeTeamScore);
            const away = parseInt(game.awayTeamScore);
            if (isNaN(home) || isNaN(away)) return;
            completedGames++;
            if (home > away) homeGames.value++;
            else if (away > home) awayGames.value++;
        });
        // An empty card must not resolve: 0-0 games would match D-D on draw-capable
        // systems before anything is typed
        if (completedGames === 0) return;
        const outcome = scoringSystem.value.outcomes.find(o => o.homeGames === homeGames.value && o.awayGames === awayGames.value);
        homeResult.value = outcome?.homeResult || "-";
        awayResult.value = outcome?.awayResult || "-";
    };

    const onFixtureUpdated = () => {
        if (!fixture.value) return;
        homeTeam.value = fixture.value.homeTeam;
        awayTeam.value = fixture.value.awayTeam;
        const newScoringSystem = _.cloneDeep(fixture.value.scoringSystem);
        // A played fixture recorded without game detail reopens in overall mode; an
        // unplayed one keeps the system's game entry available for first-time entry.
        // Set-score-required fixtures never drop to overall mode — the grid is the
        // only full-detail entry (rowless historic results reopen empty to be filled).
        if (fixture.value.playedAt != null && fixture.value.games.length === 0 &&
            fixture.value.requiresGameScores !== true) {
            newScoringSystem.splitGames = false;
        }
        gameData.value = newScoringSystem.splitGames ? fixture.value.games : [];
        while (gameData.value.length < newScoringSystem.numberGames) gameData.value.push({});
        scoringSystem.value = newScoringSystem;
        homeResult.value = fixture.value.homeTeamResult;
        awayResult.value = fixture.value.awayTeamResult;
    };

    const onLinkerUpdated = async () => {
        if (!linker.value) return;
        gameData.value = [];
        homeResult.value = awayResult.value = null;
        if (props.value.leagueCycleDivisionId != null) {
            scoringSystem.value = {...linker.value.scoringSystem};
            let updatedFixture = fixture.value || {};
            updatedFixture.homeTeamId = props.value.homeTeamId;
            updatedFixture.awayTeamId = props.value.awayTeamId;
            await getHomeTeam(props.value.homeTeamId);
            await getAwayTeam(props.value.awayTeamId);
            updatedFixture.leagueCycleDivisionId = props.value.leagueCycleDivisionId;
            // When creating a new scheduled fixture for a league division, populate fields
            // required by the schedule API and pre-fill a sensible default date/time.
            if (props.value.isScheduleCreate) {
                updatedFixture.sportId = linker.value.sportId;
                updatedFixture.scoringSystemId = linker.value.scoringSystem?.id || linker.value.scoringSystemId;
                const currentDate = useNuxtApp().$luxon.now();
                const nextDay = currentDate.plus({days: 1});
                updatedFixture.date = nextDay.toFormat('yyyy-MM-dd');
                updatedFixture.time = suggestedTimeSelectHalfHour.value;
            }
            fixture.value = updatedFixture;
        }
        if (props.value.ladderChallengeId != null || props.value.schedulingLadderChallengeId) {
            scoringSystem.value = {...linker.value.scoringSystem};
            let updatedFixture = fixture.value || {};
            updatedFixture.awayTeam = linker.value.challengedTeam;
            updatedFixture.awayTeamId = linker.value.challengedTeamId;
            updatedFixture.homeTeam = linker.value.challengingTeam;
            updatedFixture.homeTeamId = linker.value.challengingTeamId;
            homeTeam.value = linker.value.challengingTeam;
            awayTeam.value = linker.value.challengedTeam;
            updatedFixture.ladderChallengeId = props.value.ladderChallengeId || props.value.schedulingLadderChallengeId;
            //Use current date and time for new fixtures must be in string format
            if (props.value.schedulingLadderChallengeId) {
                const currentDate = useNuxtApp().$luxon.now();
                const nextDay = currentDate.plus({days: 1});
                updatedFixture.date = nextDay.toFormat('yyyy-MM-dd');
                updatedFixture.time = suggestedTimeSelectHalfHour.value;
            }
            updatedFixture.sportId = linker.value.sportId;
            updatedFixture.scoringSystemId = linker.value.scoringSystemId;
            fixture.value = updatedFixture;
        }
        if (props.value.tournamentStageId != null) {
            scoringSystem.value = {...linker.value.scoringSystem};
            let updatedFixture = fixture.value || {};
            updatedFixture.homeTeamId = props.value.homeTeamId;
            updatedFixture.awayTeamId = props.value.awayTeamId;
            await getHomeTeam(props.value.homeTeamId);
            await getAwayTeam(props.value.awayTeamId);
            updatedFixture.tournamentStageId = props.value.tournamentStageId;
            updatedFixture.sportId = linker.value.sportId;
            updatedFixture.scoringSystemId = linker.value.scoringSystemId;
            fixture.value = updatedFixture;
        }
    };

    // Watchers for dynamically updating UI
    watch(gameData, calculateScore, {deep: true});

    watch(scoringSystem, (newVal) => {
        if (!newVal || gameData.value.length > 0) return;
        gameData.value = Array.from({length: newVal.numberGames}, () => ({homeTeamScore: null, awayTeamScore: null}));
        if (!newVal.splitGames) homeResult.value = awayResult.value = null;
    });

    const {
        getItem: getFixture,
        item: fixture,
        formPending: fixtureFormLoading,
        formErrors: fixtureErrors,
        createItem: createFixture,
        deleteItem: deleteFixture,
        hasFormErrors: hasFixtureErrors
    } = useCrudApi('api/v2/fixtures', false, (item) => {
        if (props.value.isScheduleUpdate) {
            // "Suggest another" is a new suggestion: start from the default slot,
            // not the other side's time (that would be a confirm).
            const date = ['suggestAnother', 'setAnother'].includes(props.value.scheduleMode) ? null
                : item.scheduledAt ? item.scheduledAt : (item.homeTeamScheduledAt || item.awayTeamScheduledAt);
            if(date) {
                item.date = outputDateFromDb(date, 'yyyy-MM-dd');
                item.time = outputDateFromDb(date, 'HH:mm');
            } else {
                const currentDate = useNuxtApp().$luxon.now();
                const nextDay = currentDate.plus({days: 1});
                item.date = nextDay.toFormat('yyyy-MM-dd');
                item.time = suggestedTimeSelectHalfHour.value;
            }
        }
        return item;
    }, (item) => {
        return item;
    });

    const {
        item: scheduleFixture,
        formPending: scheduleFormLoading,
        formErrors: scheduleFormErrors,
        updateItem: updateFixtureSchedule,
        createItem: createFixtureSchedule,
        hasFormErrors: hasScheduleFormError
    } = useCrudApi('api/v2/fixtures/schedule', false, (item) => {
        return item;
    }, (item) => {
        return item;
    });

    const fixtureSaveLoading = computed(() => fixtureFormLoading.value || scheduleFormLoading.value);

    const {
        getItem: getHomeTeam,
        item: homeTeam,
    } = useCrudApi('api/v2/teams');

    const {
        getItem: getAwayTeam,
        item: awayTeam,
    } = useCrudApi('api/v2/teams');

    const {
        getItem: getLeagueCycleDivision,
        item: leagueCycleDivision,
        itemPending: leagueCycleDivisionLoading,
        formErrors: leagueCycleDivisionErrors
    } = useCrudApi(`api/v2/leagueCycleDivisions`);

    const {
        getItem: getLadderChallenge,
        item: ladderChallenge,
        itemPending: ladderChallengeLoading,
        formErrors: ladderChallengeErrors
    } = useCrudApi(`api/v2/ladderChallenges`);

    const getLinker = async (id) => {
        if (linkerNamespace.value === "leagueCycleDivisions") {
            await getLeagueCycleDivision(id);
        } else if (linkerNamespace.value === "ladderChallenges") {
            await getLadderChallenge(id);
        }
    };

    const linker = computed(() => {
        if (linkerNamespace.value === "leagueCycleDivisions") return leagueCycleDivision.value;
        if (linkerNamespace.value === "ladderChallenges") return ladderChallenge.value;
        return null;
    });

    // Whether the fixture's competition approves results on entry. The division and the
    // ladder challenge's ladder carry it; the fixture's own competition objects are a fallback.
    const competitionAutoApproves = computed(() => {
        const l = linker.value;
        const f = fixture.value;
        return (l?.autoApprove ?? l?.ladder?.autoApprove
            ?? f?.leagueCycleDivision?.autoApprove ?? f?.ladderChallenge?.ladder?.autoApprove
            ?? f?.tournament?.autoApprove ?? f?.americanoCycleDivision?.autoApprove) === true;
    });

    const linkerLoading = computed(() => {
        if (linkerNamespace.value === "leagueCycleDivisions") return leagueCycleDivisionLoading.value;
        if (linkerNamespace.value === "ladderChallenges") return ladderChallengeLoading.value;
        return false;
    });

    const linkerErrors = computed(() => {
        if (linkerNamespace.value === "leagueCycleDivisions") return leagueCycleDivisionErrors.value;
        if (linkerNamespace.value === "ladderChallenges") return ladderChallengeErrors.value;
        return null;
    });

    // Universal loadFixture with dynamic props
    const loading = ref(false);
    const loadFixture = async (p) => {
        fixture.value = {};
        // a refusal from the last time the dialog was open is not about this one
        scheduleFormErrors.value = {};
        inputProps.value = p;
        loading.value = true;
        if (props.value.fixtureId) {
            await getFixture(props.value.fixtureId);
            onFixtureUpdated();
            await loadScorers();
        } else if (linkerId.value) {
            await getLinker(linkerId.value);
            await onLinkerUpdated();
        }
        loading.value = false;
    };

    const playerSide = (playerId) => {
        if (fixture.value?.homeTeam?.players?.some(p => p.id === playerId)) return 'home';
        if (fixture.value?.awayTeam?.players?.some(p => p.id === playerId)) return 'away';
        return null;
    };

    // Prefill the scorer rows from EVERY goal/own-goal on the fixture — the ones
    // entered live and the ones entered by hand are one interchangeable set. The
    // bulk save now replaces both, so editing the result shows the live scorers
    // and rewrites them (no duplicates).
    const loadScorers = async () => {
        scorers.value = [];
        if (!supportsScorers.value || !fixture.value?.id) return;
        scorersLoaded.value = false;
        try {
            const res = await httpRequest(endpoints.fixtureEvents.getItems, {
                page: 1, limit: 500, filters: {fixtureIds: [fixture.value.id]},
            });
            const rows = Array.isArray(res) ? res : (res?.items ?? []);
            scorers.value = rows
                .filter(e => e.eventKey === 'goal' || e.eventKey === 'ownGoal')
                .map(e => {
                    const scorerSide = playerSide(e.playerId);
                    const ownGoal = e.eventKey === 'ownGoal';
                    // an own goal is CREDITED to the other side
                    const side = ownGoal
                        ? (scorerSide === 'home' ? 'away' : 'home')
                        : (scorerSide ?? 'home');
                    return {side, playerId: e.playerId, minute: e.value ?? null, ownGoal};
                });
            // Only now is it safe to let a later save replace the fixture's set.
            scorersLoaded.value = true;
        } catch {
            // Leave scorersLoaded false: the set is unknown, so saveScorers must
            // not overwrite the server's real (possibly live-scored) goals.
            scorers.value = [];
        }
    };

    // Push the scorer rows as the fixture's manual event set. Failure never
    // undoes the saved result — it surfaces as a form error instead.
    const saveScorers = async () => {
        if (!supportsScorers.value || !fixture.value?.id) return;
        // The existing set never loaded (fetch blip) — pushing the empty rows now
        // would delete the fixture's real goal scorers, live-entered ones included.
        // Preserve them: the result still saved, the scorers are simply left as-is.
        if (!scorersLoaded.value) return;
        try {
            // Send EVERY goal row, including "Scorer not recorded" ones (playerId
            // null) — each is a real goal, and the bulk replace (which now also
            // clears live-scored goals) would otherwise delete an unnamed live goal
            // without re-adding it. Always stamp a team so an unnamed goal keeps its
            // side and can't reject the batch: a REGULAR goal carries its own side;
            // an OWN goal carries the CONCEDING side (opposite the credited row
            // side), which the server flips to credit the opponent.
            const homeTeamId = fixture.value?.homeTeam?.id;
            const awayTeamId = fixture.value?.awayTeam?.id;
            await httpRequest(endpoints.fixtureEvents.bulk, {
                fixtureId: fixture.value.id,
                events: scorers.value.map(r => {
                    const sideTeamId = r.side === 'home' ? homeTeamId : awayTeamId;
                    const oppTeamId = r.side === 'home' ? awayTeamId : homeTeamId;
                    const teamId = r.ownGoal ? oppTeamId : sideTeamId;
                    return {
                        eventKey: r.ownGoal ? 'ownGoal' : 'goal',
                        playerId: r.playerId,
                        ...(teamId ? {teamId} : {}),
                        value: r.minute != null && r.minute !== '' ? Number(r.minute) : null,
                    };
                }),
            });
        } catch (e) {
            fixtureErrors.value = {
                global: [e?.global?.[0] ?? 'The result saved, but the goal scorers could not be saved. Reopen the result to try again.'],
            };
        }
    };

    const isScheduleForm = computed(() => props.value.isScheduleUpdate || props.value.isScheduleCreate);

    return {
        handicapStart,
        isScheduleForm,
        loadFixture,
        scorers,
        supportsScorers,
        homeTeam,
        awayTeam,
        scoringSystem,
        gameData,
        homeResult,
        awayResult,
        homeGames,
        awayGames,
        formType,
        isAdmin,
        isMyFixture,
        isApproved,
        canEdit,
        canCreate,
        fixture,
        needsMyApproval,
        resultAction,
        requiresGameScores,
        tiebreakGameNumber,
        statusNote,
        title,
        scheduleNote,
        scheduleBlock,
        loading,
        save,
        fixtureSaveLoading,
        fixtureErrors,
        noEditReason,
        buttonText,
        linker,
        linkerLoading,
        linkerErrors,
        scheduleFormErrors,
        updateFixtureSchedule,
        createFixtureSchedule,
        hasScheduleFormError
    };
}

